<template>
  <div class="h-100">
    <div v-if="isStaging && stagingBannerActive" class="staging-banner">
      <span class="ml-auto">
        You are currently in Staging (tellimer.io). To view Production, go to
        <a
          href="https://enterprise.tellimer.com"
          target="blank"
        >tellimer.com</a>
      </span>

      <div @click="stagingBannerActive = false" class="ml-auto mr-3">
        <icon icon="close" />
      </div>
    </div>
    <div v-if="!user || $route.name === 'logout'" class="h-100">
      <router-view :key="$route.path" />
    </div>

    <div v-else-if="notGDPRCompliant">
      <gdpr @compliant="user.updated_admin_gdpr_at = true" />
    </div>

    <div v-else class="app">
      <div class="d-block" style="flex: 1">
        <!-- Sidebar -->
        <sidebar class="d-none d-lg-flex" :items="userNavigation" />

        <!-- TODO: move to a component (top navigation) -->
        <div class="py-1 px-3 d-flex d-lg-none justify-content-between align-items-center top-nav">
          <a :href="tellimerUrl" style="width: 48px; height: auto">
            <img
              src="~@/assets/favicon.svg"
              alt="Tellimer"
              style="padding: .55rem 0; height: 48px;"
            />
          </a>

          <div class="ml-auto mobile-search-icon" @click="showMobileSearch = !showMobileSearch">
            <icon class="top-nav-icon" :icon="showMobileSearch ? 'close' : 'search'" />
          </div>
          <div @click="showMenu = !showMenu" class="ml-3">
            <icon class="top-nav-icon" icon="mobile-menu" />
          </div>

          <div v-if="showMobileSearch" class="border-bottom mobile-omnisearch">
            <omnisearch ref="omni" @close="showMobileSearch = false" />
          </div>
        </div>

        <header-bar ref="headerbar" :user="user" />

        <!-- CONTENT -->
        <div class="content">
          <vue-progress-bar />
          <router-view :key="$route.fullPath" class="fluid" />
          <!-- <transition
            :css="false"
            mode="out-in"
            @enter="enter"
            @leave="leave"
          >
            <router-view
              :key="$route.name"
              class="fluid"
            />
          </transition>-->
          <footer-bar />
        </div>
      </div>

      <div
        class="sidebar-overlay"
        :class="{'sidebar-overlay--active': showMenu}"
        @click="showMenu = false"
      />
      <sidebar
        class="d-flex d-lg-none"
        :items="userNavigation"
        :is-open="showMenu"
        :mobile="true"
        @click="showMenu = false"
      />
    </div>

    <notifications
      :duration="3000"
      position="top center"
      class="top-center"
      animation-type="velocity"
    />

    <company-created v-if="user" :user="user" />

    <drift-chat />
  </div>
</template>

<script>
import axios from '@/axios'
import withVelocity from '@/velocity'
import DriftChat from '@/components/DriftChat'
import HeaderBar from '@/components/HeaderBar'
import Omnisearch from '@/components/omnisearch/Omnisearch'
import Sidebar from '@/components/Sidebar'
import FooterBar from '@/components/FooterBar'
import Gdpr from '@/components/Gdpr'
import CompanyCreated from '@/components/CompanyCreated'

export default {
  components: {
    HeaderBar,
    Sidebar,
    Omnisearch,
    Gdpr,
    DriftChat,
    FooterBar,
    CompanyCreated
  },

  data() {
    return {
      user: this.$login.getUser(),
      showMenu: false,
      showMobileSearch: false,
      tellimerUrl: process.env.VUE_APP_TELLIMER_URL,
      stagingBannerActive: true
    }
  },

  // metaInfo: {
  //   title: 'Tellimer Enterprise',
  //   titleTemplate: '%s - Tellimer Enterprise',
  //   htmlAttrs: {
  //     lang: 'en',
  //     amp: undefined, // "amp" has no value
  //   },
  // },

  computed: {
    notGDPRCompliant() {
      return !this.user.updated_admin_gdpr_at
    },

    userNavigation() {
      const nav = [
        {
          text: 'Dashboard',
          icon: 'dashboard',
          link: {
            name: 'dashboard'
          }
        },
        {
          text: 'Articles',
          icon: 'articles',
          link: {
            name: 'articles'
          },
          if:
            this.$login.can('articles.view') ||
            this.$login.can('articles.view.mine') ||
            this.$login.can('articles.view.channel')
        },
        {
          text: 'Indices',
          icon: 'indices',
          link: {
            name: 'indices'
          },
          if: this.$login.can('indices.view')
        },
        {
          text: 'Countries',
          icon: 'global',
          link: {
            name: 'countries'
          },
          if: this.$login.can('country-debt.view')
        },
        {
          text: 'Companies',
          icon: 'companies',
          link: {
            name: 'companies'
          },
          if: this.$login.can('companies.view')
        },
        {
          text: 'Channels',
          icon: 'channels',
          link: {
            name: 'channels'
          },
          if: this.$login.can('channels.view')
        },
        {
          text: 'Users',
          icon: 'users',
          link: {
            name: 'users'
          },
          if: this.$login.can('users.view') || this.$login.can('users.view.channel')
        },
        {
          text: 'Analysts',
          icon: 'analysts',
          link: {
            name: 'analysts'
          },
          if: this.$login.can('analysts.view')
        },
        {
          text: 'Newsletters',
          icon: 'newsletters',
          link: {
            name: 'newsletters'
          },
          if:
            this.$login.can('newsletters.view') ||
            this.$login.can('newsletters.view.mine')
        },
        {
          text: 'Accounts',
          icon: 'accounts',
          link: {
            name: 'accounts'
          },
          if: this.$login.can('accounts.view')
        },
        {
          text: 'Subscriptions',
          icon: 'subscriptions',
          link: {
            name: 'subscriptions'
          },
          if: this.$login.can('subscriptions.view')
        },
        {
          text: 'Packages',
          icon: 'packages',
          link: {
            name: 'packages'
          },
          if: this.$login.can('roles.view')
        },
        {
          text: 'Equities IOIs',
          icon: 'equities-ioi',
          link: {
            name: 'equities'
          },
          if: false && this.$login.can('iois')
        }
        // TODO: uncomment when events-view will be wired to server APIs
        // {
        //   text: 'Events',
        //   icon: 'event-date',
        //   link: {
        //     name: 'events',
        //   },
        //   //TODO: remove the "!" after backend will be implemented
        //   if: false && this.$login.can('events.view'),
        // }
      ]

      return nav.filter((n) => typeof n.if === 'undefined' || n.if)
    },

    isStaging() {
      const stagingUrlSection = 'tellimer.io'
      return (
        window.location.host.includes(stagingUrlSection) ||
        process.env.VUE_APP_API_ENDPOINT.includes(stagingUrlSection)
      )
    }
  },

  beforeMount() {
    this.addTokenToEcho()
    this.$login.events.$on('logged-in', () => {
      this.user = this.$login.getUser()
      this.addTokenToEcho()
    })
    this.$login.events.$on('logged-out', () => {
      this.user = null
    })
  },

  mounted() {
    window.addEventListener('resize', () => {
      if (window.innerWidth > 992) {
        this.showMenu = false
      }
    })
  },

  methods: {
    addTokenToEcho() {
      if (this.$login.is()) {
        this.$echo.connector.options.auth.headers[
          'Authorization'
        ] = `Bearer ${this.$login.getToken()}`
        axios.defaults.headers.common['X-Socket-ID'] = this.$echo.socketId()

        this.$echo
          .private('admin')
          .listen('DraftSubmittedToEditorialEvent', ({ draft }) => {
            if (this.$login.can('article-drafts.publish')) {
              this.$notify({
                text: `${draft.latest_revision.title} submitted for review.`
              })
            }
          })
      }
    },

    confirmed(props) {
      props.item.data.onConfirm()
      props.close()
    },

    enter(el, done) {
      withVelocity().then((velocity) => {
        velocity.default(
          el,
          { opacity: 1, translateX: [0, -50] },
          { duration: 150, complete: done }
        )
      })
    },

    leave(el, done) {
      withVelocity().then((velocity) => {
        velocity.default(
          el,
          { opacity: 0, translateX: 50 },
          { duration: 150, complete: done }
        )
      })
    }
  }
}
</script>


<style lang="scss">
@import '@/scss/main.scss';

.staging-banner {
  width: 100vw;
  min-height: 3rem;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffdab8;
  color: #242424;
  font-size: 14px;

  @media (max-width: 576px) {
    padding-left: 1rem;
  }
}

.app {
  position: relative;
  width: 100%;
  height: auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.top-nav {
  background: $nav-bg;
  z-index: 999;
  height: 60px;

  .top-nav-icon {
    fill: white;
    color: white;

    &.active {
      fill: $lavender;
    }
  }

  .mobile-omnisearch {
    position: absolute;
    bottom: -60px;
    height: 60px;
    left: 0;
    width: 100%;
    display: none;
    background-color: $header-bar-bg;
    box-shadow: 0 7px 32px rgba(0, 0, 0, 0.12);

    .el-input {
      margin: 0 1rem;
    }

    .el-input__inner {
      background: transparent;
      border: 0;
    }

    .omnisearch__results {
    }

    @media (max-width: 769px) {
      display: flex !important;
    }
  }
}

.mobile-search-icon {
  display: flex;
  @media (min-width: 769px) {
    display: none;
  }
}

.content {
  position: relative;
  min-height: calc(100vh - 60px);
  padding: 0 72px;
  flex: 1;
  transition: $transitionTime margin;

  @media (min-width: 992px) {
    margin-left: $sidebar-width;
  }

  @media (max-width: 767px) {
    margin-top: 60px !important;
  }
}

.fluid {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.nav.nav-tabs {
  border-bottom: 0;

  .nav-item {
    z-index: 1;
    margin-bottom: -2px;
  }
}
</style>
